'use client';

import { MouseEvent } from 'react';
import Image from 'next/image';

import { useTranslations } from '@northladder/i18n';

import {
  PlayerBall,
  FirstGoalPost,
  SecondGoalPost,
  ThirdGoalPost,
} from '../../components';

/**
 * Props for the MotionSensorPrompt component.
 */
interface IMotionSensorsPromptProps {
  /** Function triggered when the user presses to start the motion sensor test. */
  onPress: () => void;

  /** Function triggered when the user presses to go back from the prompt. */
  onPressBack: () => void;
}

/**
 * ----------------------------------------------------------------------------
 * Represents the prompt for motion sensor evaluation.
 */
export function MotionSensorPrompt({
  onPress,
  onPressBack,
}: IMotionSensorsPromptProps) {
  const t = useTranslations('MotionSensorsInit.evaluate');

  /**
   * Handler function for going back to the prompt.
   */
  const handleBackToPrompt = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onPressBack();
  };

  return (
    <>
      <button
        className="relative h-full w-full"
        onClick={onPress}
        type="button"
      >
        <Image
          alt="Grass"
          className="absolute -z-10 h-full w-full object-cover"
          fill
          priority
          src="/images/assessment-images/grass.png"
        />
        <FirstGoalPost className="absolute right-6 top-8 h-24 w-24" />
        <SecondGoalPost
          className="absolute z-10 h-44 w-44"
          style={{ top: '50%', left: 0, transform: 'translateY(-50%)' }}
        />
        <ThirdGoalPost className="absolute bottom-1 right-0 h-44 w-44" />
        <PlayerBall className="absolute left-0 top-0 z-10 h-16 w-16" />
        <div className="absolute bottom-1/4 z-10 flex flex-col items-center justify-center px-4">
          <h1 className="max-w-[270px] text-center text-2xl font-bold leading-8 text-stone-900">
            {t('title')}
          </h1>
          <p
            className="
              max-w-[270px] text-center text-base font-semibold leading-tight
              text-stone-900 opacity-80
            "
          >
            {t('description')}
          </p>
          {/* eslint-disable */}
          <div
            className="z-50 p-4 text-sm text-gray-500"
            id="back-to-prompt-button"
            onClick={handleBackToPrompt}
            role="button"
          >
            {t('back')}
          </div>
        </div>
        <div className="h-full w-full bg-fuchsia-50 opacity-60" />
      </button>
      <div className="base-grad pointer-events-none absolute left-0 top-0 h-full w-full" />
    </>
  );
}
